import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, Injector, OnDestroy, PLATFORM_ID } from '@angular/core';
import { MaterialPage } from 'src/app/components/page/material-page.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ItemToSell, ItemToSellState, ItemsToSellService, Category, ItemToSellStatistics, ItemToSellList } from 'src/app/_services';
import { SidenavService } from 'src/app/_services/sidenav.service';
import { Meta, Title } from '@angular/platform-browser';
import { ComponentPortal } from '@angular/cdk/portal';
import { SearchSidenavComponent, SIDENAV_CATEGORIES } from '../sidenav/search-sidenav.component';
import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { ScrollService } from 'src/app/_services/scroll.service';

class SearchFilter {
  constructor(public categoryCode?: string, public owner?: string) {}  
}

@Component({
  selector: 'search-page',
  templateUrl: './search-page.component.html',
  styleUrls: ['./search-page.component.scss']
})
export class SearchPageComponent extends MaterialPage implements OnDestroy {

  items: ItemToSell[] = [];
  loading: boolean = true;
  latest: boolean;
  noListedSearchResults: boolean = false;
  currentQuery: string | undefined;
  currentFilter: SearchFilter = new SearchFilter();
  statistics: ItemToSellStatistics | undefined;
  headerV2 = true;
  private queryParamsSubscription: Subscription;
  private routerSubscription: Subscription;
  
  constructor(
      breakpointObserver: BreakpointObserver,
      private itemToSellService: ItemsToSellService,
      activatedRoute: ActivatedRoute,
      private router: Router,
      sidenavService: SidenavService,
      titleService: Title,
      private viewportScroller: ViewportScroller,
      meta: Meta,
      @Inject(PLATFORM_ID) private platformId: any,
      private scrollService: ScrollService
  ) {
        
    super(
      breakpointObserver, 
      titleService, 
      meta,
      sidenavService,
      router,
      $localize `:page title:Search`,
      $localize `:page description:Listx makes the selling of all secondhand things completely effortless. Create your listing with just a few clicks.`,
      true);
    
    this.routerSubscription = this.router.events.subscribe(event => {
        
      if (event instanceof NavigationEnd) {
        if (event.url.split('?')[0] === '/') {
          const scrollPosition = this.scrollService.getPosition(this.router.url);
          console.log(`Returned to search page @${scrollPosition}`);
          
          if (scrollPosition) {
            setTimeout(() => {
              this.viewportScroller.scrollToPosition([0, scrollPosition]);
            }, 1);
          }
        }
      }
    });
      
    this.queryParamsSubscription = activatedRoute.queryParams.subscribe(queryParams => {
      
      this.currentFilter.categoryCode = queryParams["categoryCode"];
      this.currentFilter.owner = queryParams["owner"];
      this.clearItems();
      
      if (queryParams["q"]) {
        this.currentQuery = queryParams["q"];
        if (isPlatformBrowser(this.platformId)) {
          this.searchBothListedAndUnlisted(this.currentQuery!, this.currentFilter);
        }
      } else {
        this.currentQuery = undefined;
        if (isPlatformBrowser(this.platformId)) {
          this.loadAndShowLatest(true, false, this.currentFilter);
        }
      }
    });
  }
  
  get showingSearchResults(): boolean {
    return (!!this.currentQuery || !!this.currentFilter?.categoryCode || !!this.currentFilter?.owner) && !this.loading;
  }
  
  override injectSidenav() {
    
    if (this.loading) {
      console.log("Search in progress, will clear sidenav");
      this.sidenavService.setPageSpecificNavigationPortal(null);
      return;
    }
    
    const showingSearchResults = this.showingSearchResults;
    
    if (showingSearchResults) {
      console.log("Will inject sidenav with category filter");
    } else {
      console.log("Will inject sidenav with top level categories");
    }
    
    const injector = Injector.create({
      providers: [{ provide: SIDENAV_CATEGORIES, useValue: showingSearchResults ? this.extractCurrentCategories() : [] }]
    });
    
    this.sidenavService.setPageSpecificNavigationPortal(new ComponentPortal(SearchSidenavComponent, null, injector));
  }
  
  private extractCurrentCategories(): Category[] {
    
    if (!this.statistics) {
      return [];
    }
    
    return this.statistics.allCategories;
  }
  
  async searchBothListedAndUnlisted(q: string, filter?: SearchFilter) {
  
    this.loading = true;
    
    const [listedStatistics] = await Promise.all([this.search(q, false, filter)]);
    
    if (this.items.length === 0) {
      this.latest = true;
      await this.loadAndShowLatest(true, false, filter);
    } else {
      this.latest = false;
    }

    this.statistics = listedStatistics;

    this.loading = false;
    this.injectSidenav();
  }

  async search(q: string, append: boolean = false, filter?: SearchFilter): Promise<ItemToSellStatistics | undefined> {
    
    const itemsToSearch = this.items;
    let loadedItems;
    
    try {
      loadedItems = await this.itemToSellService.listV2({
        q: q,
        categoryCode: filter?.categoryCode,
        owner: filter?.owner ? [filter?.owner] : [],
        limit: 24,
        state: [ItemToSellState.LISTED],
        offset: append ? itemsToSearch.length : 0
      });
    } catch (e) {
      console.error(e);
      return undefined;
    }
    
    if (append) {
      itemsToSearch.push(...loadedItems.items!);
    } else {
      itemsToSearch.splice(0, itemsToSearch.length, ...loadedItems.items!);
    }
    
    if (!append) {
      this.noListedSearchResults = loadedItems.items.length === 0;
    }
    
    return loadedItems.statistics;
  }

  async loadAndShowLatest(listed: boolean = true, append: boolean = false, filter?: SearchFilter) {
    
    this.loading = true;
    
    const limit = append ? 24 : 12;
    
    try {
      const [listedItems] = await Promise.all([
        listed && this.loadListings(ItemToSellState.LISTED, limit, append ? this.items?.length : 0, true, filter)
      ].filter(p => p != undefined));
      
      if (append) {
        listedItems && this.items.push(...listedItems.items);
      } else {
        listedItems && (this.items = listedItems.items);
      }
      
      if (listedItems) {
        this.statistics = listedItems.statistics;
      }
      
    } catch (e) {
      console.error(e);
    }
      
    if (this.items.length !== 0) {
      this.latest = true;
    }
    
    this.loading = false;
    this.injectSidenav();
  }
  
  private loadListings(state: ItemToSellState, limit: number, offset: number, latest: boolean, filter?: SearchFilter): Promise<ItemToSellList> {
    return this.itemToSellService.listV2({
        categoryCode: filter?.categoryCode,
        owner: filter?.owner ? [filter.owner] : [],
        limit: limit, 
        state: [ state ],
        offset: offset, 
        sort: "-hasPictures" + (latest ? ",-listingTimestamp" : ""),
      })
  }
  
  toSelectListingFlow() {
    this.router.navigate(['select-listing-flow']);
  }
  
  async toSnapAndSell() {
    this.router.navigate(['snap-and-sell']);
  }
  
  toSignup() {
    this.router.navigate(['signup']);
  }
  
  async toGreenBox() {
    this.router.navigate(['green-box']);
  }
  
  get isSearchResult(): boolean {
    return !!this.currentQuery;
  }
  
  async seeMore(listed: boolean = true) {
    
    this.loading = true;
    
    this.latest ? 
      await this.loadAndShowLatest(listed, true, this.currentFilter) 
        : await this.search(this.currentQuery!, true, this.currentFilter);
        
    this.loading = false;
  }
  
  private clearItems() {
    this.items = [];
    this.latest = false;
  }
  
  ngOnDestroy() {
    
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();  
    }
    
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
